import { useState, useEffect, useRef } from "react";
import { Typography, Button } from "@mui/material";
import { tss } from "tss-react/mui";
import { Scrollbars } from "react-custom-scrollbars";
import ConfigureAttributes from "./ConfigureAttributes";
import ViewerEvents from "events/ViewerEvents";
import DomEvents from "events/DomEvents";
import { ViewerActions, LayersActions } from "actions";
import * as Utils from "services/UtilsService";
import { LayerIcon } from "components";
import { Tune } from "@mui/icons-material";
import { panelWidth } from "styles/common";

export const useStyles = tss.create(({theme}) => ({
    root: {
        display: "flex",
        width: "100%",
        justifyContent: "center",
    },
    header: {
        position: "relative",
        width: panelWidth,
    },
    title: {
        color: "#283C50",
        fontSize: 14,
        fontWeight: "bold",
        letterSpacing: "0.1px",
        lineHeight: "16px",
        padding: 10,
    },
    button: {
        width: 30,
        height: 30,
        minWidth: 30,
    },
    centerDiv: {
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: 80,
    },
    message: {
        color: theme.palette.text.secondary,
        fontSize: 14,
        letterSpacing: 0.25,
        lineHeight: "16px",
    },
    layerInfo: {
        position: "relative",
        height: 44,
        width: "100%",
        borderRadius: "4px 4px 0 0",
        borderBottom: "solid 1px #E5E9F1",
        display: "inline-flex",
        alignItems: "center",
    },
    layerTitle: {
        display: "inline-flex",
        position: "relative",
        left: 12,
        width: 170,
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
        alignItems: "center",
        color: "#283C50",
        fontSize: 14,
        letterSpacing: "0px",
        lineHeight: "16px",
    },
    dimension: {
        height: 47,
        width: "100%",
        borderRadius: "4px 4px 0 0",
        borderBottom: "solid 1px #E5E9F1",
        display: "inline-flex",
        alignItems: "center",
    },
    dimHeader: {
        width: 80,
        height: 20,
        color: "#687684",
        fontSize: 10,
        letterSpacing: "1.5px",
        lineHeight: "11px",
        textAlign: "center",
        verticalAlign: "bottom",
    },
    dimContent: {
        color: "#283C50",
        fontSize: 14,
        letterSpacing: "0px",
        lineHeight: "16px",
        textAlign: "center",
        height: 26,
        verticalAlign: "middle",
    },
    label: {
        color: "#283C50",
        fontSize: 14,
        letterSpacing: "0px",
        lineHeight: "16px",
    },
    firstCol: {
        position: "relative",
        height: 34,
        display: "inline-flex",
        alignItems: "center",
        textAlign: "left",
        width: 114,
    },
    secCol: {
        position: "relative",
        height: 34,
        display: "inline-flex",
        alignItems: "center",
        textAlign: "left",
        width: 80,
    },
    thirdCol: {
        position: "relative",
        height: 34,
        display: "inline-flex",
        alignItems: "center",
        textAlign: "left",
        width: 26,
    },
    attribute: {
        borderBottom: "solid 1px #E5E9F1",
        color: "#3D4F61",
        fontSize: 12,
        letterSpacing: "0.4px",
        lineHeight: "14px",
        textAlign: "justify",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
    },
    attributeLabel: {
        color: "#3D4F61",
        fontSize: 12,
        letterSpacing: "0.4px",
        lineHeight: "14px",
        textAlign: "justify",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
    },
    bottom: {
        display: "inline-flex",
        alignItems: "center",
        height: 34,
        width: "100%",
        borderTop: "1px solid rgba(40,60,80,0.2)",
    },
    configure: {
        color: "#283C50",
        fontSize: 14,
        letterSpacing: "0px",
        lineHeight: "16px",
        width: 200,
        paddingLeft: 10,
    },
    icon: {
        position: "relative",
        width: 16,
        height: 16,
        backgroundSize: "16px 16px",
        verticalAlign: "middle",
        display: "inline-block",
        marginRight: 8,
    },
    td: {
        width: 80,
        height: 36,
    },
}));

let layer: any,
    unitLayer: any,
    inputs: any,
    defaultAttributes: any = [],
    customAttributes: any = [],
    selectedAttributes: any;

let featureInfoQueryId: number = 0;

const PrimitiveType: any = {
    cylinder: "Segment",
    collar: "Collar",
};

type Data = {
    x: number;
    y: number;
    z: number;
    attributes: any;
    units: any;
    type: string;
    dhInfo: {
        ID: string;
        length: string; //?
        from: string; //?
        to: string; //?
    };
    available: boolean;
};

var initialState: Data = {
    x: 0,
    y: 0,
    z: 0,
    attributes: [],
    units: "N/A",
    type: "",
    dhInfo: {
        ID: "N/A",
        length: "N/A",
        from: "N/A",
        to: "N/A",
    },
    available: false,
};

let infoShow = false;

type IProps = {
    show: boolean;
    commentShowing: boolean;
};

export default function InfoPanel(props: IProps) {
    const { classes } = useStyles();

    const { show, commentShowing } = props;
    const [update, setUpdate] = useState(false);
    const [openConfig, setOpenConfig] = useState(false);
    const [data, setData] = useState<any>({ ...initialState });
    const [filter, setFilter] = useState(customAttributes);

    const { viewer, currentProject, projectId } = ViewerEvents;
    const panel = useRef(null);

    useEffect(() => {
        if (show !== infoShow) {
            infoShow = show;
        }
        return () => {
            infoShow = false;
        };
    }, [show]);
    useEffect(() => {
        const onRaycastHit = (event: any) => {
            console.log("onRaycastHit:", event);
            if (event.type !== "unit") {
                var {
                    type,
                    cellId,
                    layerId,
                    worldPoint,
                    customAttributeNames,
                    customAttributeValues,
                    alphaAttributes,
                    data,
                } = event;
                var { objectId, worldLength, segmentDepthFrom, segmentDepthTo, campaign, sampleId } = data;
                var components = ViewerEvents.compositeMeta.components;
                var component = components[layerId];
                if (component) {
                    var entityId = component.source;
                    layer = currentProject[entityId];
                }
                var units: any = [];
                var extendedMeta = LayersActions.attributes[layer["ID"]];
                var attributes: any = [];
                if (extendedMeta) {
                    units = extendedMeta.map((meta: any) => meta.unit || "N/A");
                }
                //console.log('layer:', layer);
                if (customAttributeNames.length !== customAttributeValues.length) {
                    customAttributeValues.splice(0, 1);
                }
                var indexVoxelAttribute = customAttributeNames.findIndex(
                    (name: string) => name === "voxelfarm_attribute"
                );
                if (indexVoxelAttribute > -1) {
                    customAttributeNames.splice(indexVoxelAttribute, 1);
                    customAttributeValues.splice(indexVoxelAttribute, 1);
                    units.splice(indexVoxelAttribute, 1);
                }

                customAttributeNames.forEach((name: string, index: number) => {
                    attributes.push({ name: name, value: customAttributeValues[index], unit: units[index] });
                });

                const dispatchData = () => {
                    var newData = {
                        x: Utils.round2Decimals(worldPoint[0]),
                        y: Utils.round2Decimals(worldPoint[1]),
                        z: Utils.round2Decimals(worldPoint[2]),
                        type: type,
                        attributes: attributes,
                        units: "N/A",
                        available: true,
                        dhInfo: {
                            ID: objectId || objectId === 0 ? objectId : "N/A",
                            length:
                                typeof worldLength !== "undefined"
                                    ? worldLength !== 0
                                        ? Utils.round2Decimals(worldLength)
                                        : "N/A"
                                    : "N/A",
                            from:
                                typeof segmentDepthFrom !== "undefined"
                                    ? Utils.round2Decimals(segmentDepthFrom)
                                    : "N/A",
                            to: typeof segmentDepthTo !== "undefined" ? Utils.round2Decimals(segmentDepthTo) : "N/A",
                            campaign: campaign ? campaign : "N/A",
                            sampleId: sampleId ? sampleId : "N/A",
                        },
                    };
                    setData({ ...newData });
                };
                if (type === "block" || type === "surface") {
                    featureInfoQueryId++;
                    viewer.requestFeatureInfo(
                        featureInfoQueryId,
                        cellId,
                        layerId,
                        worldPoint,
                        [],
                        customAttributeNames,
                        true,
                        (featureInfo: any) => {
                            if (featureInfo.queryId === featureInfoQueryId) {
                                var values = featureInfo.attributeValues[0];
                                values.forEach((value: number, index: number) => {
                                    var alphaAttribute = alphaAttributes.find((attr: any) => attr.index === index);
                                    if (alphaAttribute) {
                                        value = alphaAttribute.values[value];
                                    }
                                    attributes[index].value = value;
                                });
                                dispatchData();
                            }
                        }
                    );
                }

                viewer.createDot("InfoPanel-Dot", worldPoint);
                updateAttributes(layer, attributes);
                dispatchData();
            } else {
                const { type, worldPoint, data, customAttributeNames, customAttributeValues } = event;
                const targetUnitId = data.unitId;
                const attributeNames = customAttributeNames;
                const attributeValues = customAttributeValues;
                units = [];
                if (!unitLayer) {
                    unitLayer = {
                        ID: Utils.createGuid(),
                        name: "Vehicle Information",
                        view_type: "com.voxelfarm.program.view.truck",
                        project: projectId,
                    };
                    layer = unitLayer;
                } else {
                    layer = unitLayer;
                }

                viewer.createDotWithUnitTarget("InfoPanel-Dot", worldPoint, targetUnitId);
                console.log("raycast Event Is ", event);

                let attributes: any = [];

                attributeNames.forEach((name: string, index: number) => {
                    attributes.push({ name: name, value: attributeValues[index], unit: units[index] || "N/A" });
                });

                const dispatchData = () => {
                    var newData = {
                        x: Utils.round2Decimals(worldPoint[0]),
                        y: Utils.round2Decimals(worldPoint[1]),
                        z: Utils.round2Decimals(worldPoint[2]),
                        type: type,
                        attributes: attributes,
                        units: "N/A",
                        available: true,
                        dhInfo: {
                            ID: objectId || objectId === 0 ? objectId : "N/A",
                            length:
                                typeof worldLength !== "undefined"
                                    ? worldLength !== 0
                                        ? Utils.round2Decimals(worldLength)
                                        : "N/A"
                                    : "N/A",
                            from:
                                typeof segmentDepthFrom !== "undefined"
                                    ? Utils.round2Decimals(segmentDepthFrom)
                                    : "N/A",
                            to: typeof segmentDepthTo !== "undefined" ? Utils.round2Decimals(segmentDepthTo) : "N/A",
                            campaign: campaign ? campaign : "N/A",
                            sampleId: sampleId ? sampleId : "N/A",
                        },
                    };
                    setData({ ...newData });
                };

                updateAttributes(layer, attributes);
                dispatchData();
            }
        };
        if (show) {
            DomEvents.setClickEventHook(show);
            DomEvents.onRaycastHit = onRaycastHit;
        }
    }, [show, viewer, projectId, currentProject, filter]);

    useEffect(() => {
        if (!show) {
            if (viewer && data.available) {
                DomEvents.onRaycastHit = () => {};
                setData({ ...initialState });
                viewer.destroyDot("InfoPanel-Dot");
            }
        }
        if (ViewerActions.clickEventHook !== show) {
            ViewerActions.setClickEventHook(show);
        }
        return () => {
            if (!infoShow) {
                if (viewer && data.available) {
                    DomEvents.onRaycastHit = () => {};
                    setData({ ...initialState });
                    viewer.destroyDot("InfoPanel-Dot");
                }
            }
        };
    }, [show, data, viewer]);

    useEffect(() => {
        const onUpdate = (updatedLayer: any) => {
            layer = updatedLayer;
            //console.log('updateColorLegend', updatedLayer);
            setUpdate((prevUpate) => !prevUpate);
        };
        ViewerEvents.on("updateColorLegend", onUpdate);
        return () => {
            ViewerEvents.removeEventListener("updateColorLegend", onUpdate);
        };
    }, []);

    useEffect(() => {
        if (panel && panel.current) {
            if (panel.current !== null) {
                const current: any = panel.current;
                if (commentShowing) {
                    current.style.right = "300px";
                } else {
                    current.style.right = "55px";
                }
            }
        }
    });

    const openConfigureAttributes = () => {
        setOpenConfig(true);
    };

    const closeConfigureAttributes = () => {
        setOpenConfig(false);
    };

    const updateParent = () => {
        updateAttributes(layer, data.attributes);
        setUpdate((prevUpate) => !prevUpate);
    };

    const updateAttributes = (layer: any, currentAttributes: any) => {
        defaultAttributes = currentAttributes.map((attribute: any) => attribute.name);
        if (layer["custom_attributes"] && layer["custom_attributes"] !== "") {
            selectedAttributes = currentAttributes;
            customAttributes = layer["custom_attributes"].split(" ");
            selectedAttributes = selectedAttributes.filter((attribute: any) =>
                customAttributes.includes(attribute.name)
            );
            setFilter(customAttributes);
        } else {
            selectedAttributes = currentAttributes;
            customAttributes = [];
            currentAttributes.forEach((attribute: any) => {
                customAttributes.push(attribute.name);
            });
            setFilter(customAttributes);
        }
    };

    return (
        <div className={classes.root} style={{ userSelect: update ? "none" : "none" }}>
            {!data.available || !layer ? (
                <div className={classes.centerDiv}>
                    <Typography className={classes.message} style={{ padding: 5 }}>
                        Click to get Info
                    </Typography>
                </div>
            ) : (
                <div className={classes.header}>
                    <div className={classes.layerInfo}>
                        <LayerIcon layer={layer} inputs={inputs} />
                        <Typography className={classes.layerTitle}> {layer.name}</Typography>
                    </div>

                    <div className={classes.dimension}>
                        <table cellPadding={0} cellSpacing={0} style={{ width: "100%", height: "100%" }}>
                            <tbody>
                                <tr>
                                    <td className={classes.dimHeader}>X</td>
                                    <td className={classes.dimHeader}>Y</td>
                                    <td className={classes.dimHeader}>Z</td>
                                </tr>
                                <tr>
                                    <td className={classes.dimContent}>{data.x}</td>
                                    <td className={classes.dimContent}>{data.y}</td>
                                    <td className={classes.dimContent}>{data.z}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div
                        style={{
                            display: data.type === "cylinder" || data.type === "collar" ? "flex" : "none",
                            height: data.type === "cylinder" ? 180 : 120,
                        }}
                    >
                        <table cellPadding={0} cellSpacing={0} style={{ width: 211, marginTop: 16 }}>
                            <tbody>
                                <tr>
                                    <td className={classes.firstCol}>
                                        <Typography className={classes.message}>Drillhole ID: </Typography>
                                    </td>
                                    <td className={classes.secCol}>
                                        <Typography className={classes.label}>{data.dhInfo.ID}</Typography>
                                    </td>
                                </tr>
                                <tr>
                                    <td className={classes.firstCol}>
                                        <Typography className={classes.message}>DH Length: </Typography>
                                    </td>
                                    <td className={classes.secCol}>
                                        <Typography className={classes.label}>{data.dhInfo.length}</Typography>
                                    </td>
                                </tr>
                                <tr>
                                    <td className={classes.firstCol}>
                                        <Typography className={classes.message}>Primitive Type: </Typography>
                                    </td>
                                    <td className={classes.secCol}>
                                        <Typography className={classes.label}>{PrimitiveType[data.type]}</Typography>
                                    </td>
                                </tr>
                                <tr
                                    style={{
                                        display: data.type === "cylinder" ? "block" : "none",
                                    }}
                                >
                                    <td className={classes.firstCol}>
                                        <Typography className={classes.message}>From: </Typography>
                                    </td>
                                    <td className={classes.secCol}>
                                        <Typography className={classes.label}>{data.dhInfo.from}</Typography>
                                    </td>
                                </tr>
                                <tr
                                    style={{
                                        display: data.type === "cylinder" ? "block" : "none",
                                    }}
                                >
                                    <td className={classes.firstCol}>
                                        <Typography className={classes.message}>To: </Typography>
                                    </td>
                                    <td className={classes.secCol}>
                                        <Typography className={classes.label}>{data.dhInfo.to}</Typography>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div>
                        <table cellPadding={0} cellSpacing={0} style={{ width: "100%", marginTop: 10 }}>
                            <tbody>
                                <tr>
                                    <td className={classes.td}>
                                        <Typography
                                            className={classes.attributeLabel}
                                            style={{ marginLeft: 16, fontWeight: "bold" }}
                                        >
                                            Attribute
                                        </Typography>
                                    </td>
                                    <td className={classes.td}>
                                        <Typography
                                            className={classes.attributeLabel}
                                            style={{ marginLeft: 20, fontWeight: "bold" }}
                                        >
                                            Value
                                        </Typography>
                                    </td>
                                    <td className={classes.td}>
                                        <Typography
                                            className={classes.attributeLabel}
                                            style={{ marginLeft: 24, fontWeight: "bold" }}
                                        >
                                            Unit
                                        </Typography>
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={3}>
                                        <Scrollbars
                                            autoHeight
                                            autoHide
                                            autoHeightMax={data.type === "cylinder" ? 225 : 400}
                                            style={{
                                                overflowX: "hidden",
                                                userSelect: "none",
                                            }}
                                        >
                                            <table cellPadding={0} cellSpacing={0} style={{ width: "100%" }}>
                                                <tbody>
                                                    {selectedAttributes.map((attribute: any) => (
                                                        <tr key={"row_" + attribute.name}>
                                                            <td className={classes.td}>
                                                                <Typography
                                                                    className={classes.attributeLabel}
                                                                    style={{
                                                                        paddingLeft: 15,
                                                                        maxWidth: 90,
                                                                        overflow: "hidden",
                                                                    }}
                                                                    noWrap
                                                                >
                                                                    {attribute.name}
                                                                </Typography>
                                                            </td>
                                                            <td className={classes.td}>
                                                                <Typography
                                                                    className={classes.attributeLabel}
                                                                    style={{
                                                                        paddingLeft: 15,
                                                                        maxWidth: 90,
                                                                        overflow: "hidden",
                                                                    }}
                                                                    noWrap
                                                                >
                                                                    {typeof attribute.value === "number"
                                                                        ? Utils.round2Decimals(attribute.value)
                                                                        : attribute.value}
                                                                </Typography>
                                                            </td>
                                                            <td className={classes.td}>
                                                                <Typography
                                                                    className={classes.attributeLabel}
                                                                    style={{
                                                                        paddingLeft: 15,
                                                                        maxWidth: 90,
                                                                        overflow: "hidden",
                                                                    }}
                                                                    noWrap
                                                                >
                                                                    {attribute.unit}
                                                                </Typography>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </Scrollbars>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div style={{ position: "relative" }}>
                        <div className={classes.bottom}>
                            <Typography className={classes.configure}>Configure Attributes</Typography>
                            <Button
                                className={classes.button}
                                onClick={openConfigureAttributes}
                                color={openConfig ? "primary" : "inherit"}
                            >
                                <Tune className={classes.icon} />
                            </Button>
                        </div>
                    </div>

                    {openConfig ? (
                        <div>
                            <ConfigureAttributes
                                layer={layer}
                                defaultAttributes={defaultAttributes}
                                selectedAttributes={customAttributes}
                                filter={filter}
                                setFilter={setFilter}
                                closeWindow={closeConfigureAttributes}
                                updateParent={updateParent}
                                commentShowing={commentShowing}
                            />
                        </div>
                    ) : (
                        ""
                    )}
                </div>
            )}
        </div>
    );
}
